import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api, EchecApi } from '../partage/api/client';
import type { LigneVivier } from '../partage/api/types';
import { cles } from '../partage/relectures';
import { BandeAVerifier } from './BandeAVerifier';
import { ChoixDuDossier, type Pastille } from './ChoixDuDossier';
import { ChoixDuPlanDeVie } from './ChoixDuPlanDeVie';
import { Debloquant, type Garder } from './Debloquant';
import { Etages } from './Etages';
import { etagesDeMaintenant } from './etagesDeMaintenant';
import { JourneeEnPostIt } from './JourneeEnPostIt';
import { PastillesDeReglage } from './PastillesDeReglage';
import {
  ecrireLesReglages,
  lireLesReglages,
  requeteDesReglages,
  type ReglagesDeMaintenant,
} from './reglagesDeMaintenant';
import { useAuPouce, useCouche } from '../partage/surface';
import { useAngleDesGestes, useCourageDesGestes } from '../partage/useContexteDesGestes';
import { CA_N_A_PAS_MARCHE, faitTitre, gardeePourAujourdhui, garderRefuse, PAS_DE_RESEAU } from '../partage/vocabulaire';
import { refusDeCreation } from '../partage/creation';
import { useCreer, useRepondreAuMonde } from '../partage/useArbre';
import { signalerMaintenant } from '../aide';
import { useCocher, useGarderPourAujourdhui, useJourneeDuJour } from './journeeDuJour';
import { jourDuTelephone, lienDeMaJournee } from './aujourdhuiEnMots';
import { BandeauDeLaVeille, BandeauDuLendemain } from './journee/BandeauxDeLaJournee';
import { debloquantAuRang, ligneDesEcarts, margeDuDebloquant, phraseAVerifier } from './maintenantEnMots';
import { MaintenantAuPouce } from './MaintenantAuPouce';
import { AVerifierAuPouce } from './AVerifierAuPouce';
import { partiesDeMaintenant, porteeDeMaintenant } from './maintenantDansUnAngle';
import { Premier } from './Premier';
import './Maintenant.css';

/** Le rangement de l'appareil, s'il y en a un : sa lecture peut lever, `reglagesDeMaintenant` s'en garde. */
const rangement = () => window.localStorage;

/**
 * « Maintenant », l'écran d'accueil, refait d'après sa maquette de référence (chantier
 * `revue-design`, ticket 01, `design/maintenant.html`, décisions d'Igor du 2026-10-03) : on voit
 * d'un coup d'œil quoi faire, sans être noyé.
 *
 * **En haut** : le titre, sans phrase de tête ; « Ma journée : 4 choses, prochaine à 9 h 30 », un
 * lien vers l'Agenda sur aujourd'hui (planifier sa journée se fait là) ; « Tous les projets » ;
 * « Plan de vie », qui n'est pas encore accessible. Puis les réglages repliés sur une ligne :
 * « J'ai », « Je suis », « Courage », gardés sur l'appareil. La page se recalcule à chaque choix ;
 * le serveur filtre et classe (`/api/maintenant`).
 *
 * **Quatre voix, jamais fusionnées en un classement opaque** (chantier maintenant-en-etages,
 * modification 01, maquette du carnet `maintenant.html`) : la machine (le débloquant, en grande
 * fiche scotchée en haut à gauche, « Une autre » pour le suivant) ; dessous, la flottaison en
 * étages (`Etages.tsx`) : le calendrier à la Surface (Prioritaire, qui disparaît vide), tes
 * réglages dans Léger et Plus lourd, le reste Sous l'eau ; à droite, le monde (À vérifier, sur un
 * post-it) et ta journée, sur une feuille à lignes.
 *
 * **Sans projet à soi**, l'écran accueille avec « Par quoi tu commences ? » (`Premier.tsx`).
 * **Au téléphone**, `MaintenantAuPouce` : une seule chose en grand (chantier `telephone-cadre`,
 * ticket 02) ; le titre est commun, le choix du projet et les réglages passent dans son bouton
 * Réglages et son panneau (ticket 03). Le projet n'est pas gardé sur l'appareil, J'ai, Je suis et
 * Courage le sont, sur l'ordinateur comme au téléphone.
 *
 * **Dans l'angle Maintenant d'une feuille** (chantier angles-du-projet, ticket 02), le même écran
 * avec `angle` : la portée est la bulle ouverte, sans tête de page ni menu des projets, et une
 * ligne ouvre sa fiche comme la feuille le décide (`maintenantDansUnAngle.ts`).
 */
export function Maintenant({
  angle,
}: {
  /** L'angle d'une feuille : la bulle ouverte, et comment y ouvrir une fiche. */
  angle?: { portee: string; onOuvrir: (id: string) => void };
} = {}) {
  const naviguer = useNavigate();
  /*
   * Couché, le téléphone garde son écran, en deux colonnes (chantier telephone-couche-suite, 01) :
   * l'écran dépasse 720 px, sans ça il retomberait sur celui de l'ordinateur.
   */
  const couche = useCouche() && angle === undefined;
  const auPouce = useAuPouce() || couche;
  const parties = partiesDeMaintenant({ dansUnAngle: angle !== undefined, auPouce });
  const repondre = useRepondreAuMonde();
  const creer = useCreer(null);
  const cocher = useCocher();
  const garder = useGarderPourAujourdhui();
  const [reglages, setReglages] = useState<ReglagesDeMaintenant>(() => lireLesReglages(rangement));
  /*
   * Un geste parti d'ici porte le courage choisi (chantier journal-des-gestes, ticket 05) ; la page
   * dit aussi son angle, dans une feuille c'est la feuille qui le dit.
   */
  useCourageDesGestes(reglages.courage);
  useAngleDesGestes(angle ? null : 'maintenant');
  const changer = (r: Partial<ReglagesDeMaintenant>) => {
    const suivants = { ...reglages, ...r };
    setReglages(suivants);
    ecrireLesReglages(rangement, suivants);
  };
  const [dossier, setDossier] = useState<Pastille | null>(null);
  const [toutVoir, setToutVoir] = useState(false);
  // « Une autre » : le rang du débloquant montré, en boucle.
  const [rang, setRang] = useState(0);
  // Les débloquants que « Garder pour aujourd'hui » s'est vu refuser : le bouton se cache.
  const [refuses, setRefuses] = useState<string[]>([]);

  /*
   * À l'ordinateur, Sous l'eau montre quelques noms replié (chantier maintenant-en-etages,
   * modification 01) : la page demande toujours ses lignes au serveur, et « + N autres » ne fait
   * que déplier. Au téléphone, « Voir le reste » les demande, comme avant.
   */
  const requete = requeteDesReglages(
    reglages,
    porteeDeMaintenant(angle?.portee ?? null, dossier?.id ?? null),
    toutVoir || !auPouce
  );
  /*
   * Les lignes d'avant restent affichées pendant qu'un réglage recalcule : sans ça, la page
   * se vidait à chaque choix.
   */
  const vivier = useQuery({
    queryKey: cles.maintenant(requete),
    queryFn: () => api.maintenant(requete),
    placeholderData: keepPreviousData,
  });
  // La journée du jour (le lien « Ma journée ») et ses bandeaux.
  const duJour = useJourneeDuJour();
  const [annonce, setAnnonce] = useState<string | null>(null);

  /*
   * Ce que la page montre, dit au tuto (chantier `aide`, ticket 03) : rien de visible. Seulement
   * d'après une réponse arrivée, jamais d'après les lignes d'avant gardées pendant un calcul.
   */
  const arrives = vivier.data && !vivier.isPlaceholderData ? vivier.data.debloquants : null;
  const montreAuTuto = arrives ? (debloquantAuRang(arrives, rang)?.bulle.id ?? null) : undefined;
  const premierAuTuto = arrives?.[0]?.bulle.id ?? null;
  const projetChoisi = dossier?.id ?? null;
  // « J'ai » (ticket 04, « Trier tes tâches ») : dit avec la réponse calculée pour lui.
  const tempsChoisi = reglages.temps;
  // Combien de lignes Prioritaire montre (ticket 02 rouvert : vide, le bloc n'existe pas, et la
  // carte de la visite le dit).
  const prioritaireAuTuto = vivier.data && !vivier.isPlaceholderData ? vivier.data.prioritaire.length : null;
  const signaleAuTuto = parties.signaleAuTuto;
  useEffect(() => {
    if (!signaleAuTuto || montreAuTuto === undefined || prioritaireAuTuto === null) return;
    signalerMaintenant({
      projet: projetChoisi,
      debloquant: montreAuTuto,
      uneAutre: montreAuTuto !== null && montreAuTuto !== premierAuTuto,
      reste: toutVoir,
      temps: tempsChoisi,
      prioritaire: prioritaireAuTuto,
    });
  }, [signaleAuTuto, montreAuTuto, premierAuTuto, projetChoisi, toutVoir, tempsChoisi, prioritaireAuTuto]);
  useEffect(() => (signaleAuTuto ? () => signalerMaintenant(null) : undefined), [signaleAuTuto]);

  const classe = `maintenant${angle ? ' maintenant--angle' : ''}${couche ? ' maintenant--couche' : ''}`;
  if (vivier.isPending) return <div className={classe} />;
  if (!vivier.data) {
    // Au téléphone, un écran vide sans un mot ressemble à une panne : il dit pourquoi.
    return (
      <div className={classe}>{(auPouce || angle) && vivier.isError && <p className="une-vide">{PAS_DE_RESEAU}</p>}</div>
    );
  }

  /*
   * Le premier dossier ouvert d'ici se retrouve là où on l'aurait ouvert jusqu'ici : dans
   * « Projets », sa ligne à la place de la page vide.
   */
  if (vivier.data.sansProjet && parties.premier) {
    const ouvrirLePremier = (titre: string) =>
      creer.mutate(titre, { onSuccess: () => naviguer('/dossiers') });
    return <Premier onOuvrir={ouvrirLePremier} refus={refusDeCreation(creer.error)} />;
  }

  const v = vivier.data;
  const { aVerifier, pastilles } = v;
  const jd = duJour.data;
  const jour = jd?.aujourdhui ?? jourDuTelephone();

  const ouvrir = angle ? angle.onOuvrir : (id: string) => naviguer(`/feuille/${id}`);
  const basculerLeReste = () => setToutVoir(!toutVoir);
  const arrive = (id: string) => repondre.mutate({ id, champs: { etat: 'fait' } });
  const relancer = (id: string) => repondre.mutate({ id, champs: { dateAction: jour } });

  // Le débloquant montré : le serveur en rend quelques-uns, « Une autre » passe au suivant.
  const { debloquants } = v;
  const montre = debloquantAuRang(debloquants, rang);
  const occupe = cocher.isPending || garder.isPending;

  const faire = (b: { id: string; titre: string }) =>
    cocher.mutate(
      { id: b.id, fait: true },
      {
        onSuccess: () => setAnnonce(faitTitre(b.titre)),
        onError: () => setAnnonce(CA_N_A_PAS_MARCHE),
      }
    );

  const garderLa = (b: LigneVivier) =>
    garder.mutate(b.id, {
      onSuccess: () => setAnnonce(gardeePourAujourdhui(b.titre)),
      onError: (e) => {
        // Un refus attendu (200) : le serveur ne la prend pas aujourd'hui, le bouton se cache.
        if (e instanceof EchecApi && e.statut === 200) {
          setRefuses((r) => [...r, b.id]);
          setAnnonce(garderRefuse(e.code));
        } else setAnnonce(CA_N_A_PAS_MARCHE);
      },
    });

  /*
   * Un rendez-vous a déjà son heure, et le serveur refuse de le garder : le bouton se cache
   * (décision d'Igor du 2026-10-03), comme pour un débloquant déjà refusé.
   */
  const etatDeGarder = (b: LigneVivier): Garder => {
    if (b.debut || refuses.includes(b.id)) return 'cache';
    if (jd?.journee?.taches.some((t) => t.bulleId === b.id)) return 'dans-la-journee';
    return 'bouton';
  };

  /*
   * Au téléphone, ni « Ma journée », ni « Plan de vie », ni les bandeaux de la veille et du
   * lendemain : la journée a son onglet (chantier `telephone-cadre`, tickets 01 et 02).
   */
  return (
    <div className={classe} data-tuto="maintenant">
      {/* Au téléphone, le titre est dans le haut du carnet (`HautDuPouce`, chantier telephone-habit, 02). */}
      {parties.tete && !auPouce && (
        <header className="maintenant-tete">
          <div data-tuto="maintenant-tete">
            <h1>Maintenant</h1>
          </div>
          <div className="maintenant-choix">
            {jd && parties.maJournee && (
              <Link className="maintenant-journee" data-tuto="journee" to={`/agenda?jour=${jd.aujourdhui}`}>
                {lienDeMaJournee(jd.journee)}
              </Link>
            )}
            {parties.choixDuProjet && (pastilles.length > 0 || dossier !== null) && (
              <ChoixDuDossier pastilles={pastilles} choisi={dossier} onChoisir={setDossier} />
            )}
            {parties.maJournee && <ChoixDuPlanDeVie />}
          </div>
        </header>
      )}

      {parties.bandeaux && jd?.lendemain && <BandeauDuLendemain bandeau={jd.lendemain} aujourdhui={jd.aujourdhui} onAnnonce={setAnnonce} />}
      {parties.bandeaux && jd?.veille && <BandeauDeLaVeille bandeau={jd.veille} aujourdhui={jd.aujourdhui} onAnnonce={setAnnonce} />}
      {annonce && (
        <p className="journee-annonce" role="status">
          {annonce}
        </p>
      )}

      {parties.reglages && <PastillesDeReglage reglages={reglages} onChanger={changer} />}

      {auPouce ? (
        <MaintenantAuPouce
          vivier={v}
          reglages={reglages}
          dossier={dossier}
          occupe={cocher.isPending}
          onOuvrir={ouvrir}
          onFait={faire}
          onRegler={(r, d) => {
            changer(r);
            setDossier(d);
          }}
          couche={couche}
          aVerifier={<AVerifierAuPouce lignes={aVerifier} onOuvrir={ouvrir} onArrive={arrive} onRelancer={relancer} />}
        />
      ) : (
        <div className="maintenant-grille">
          <div className="maintenant-gauche">
            {/* Un autre débloquant repart replié : ce qui était déroulé était le sien. */}
            {montre && (
              <Debloquant
                key={montre.bulle.id}
                debloquant={montre}
                ecart={ligneDesEcarts(montre.ecarts, v.reglages.temps)}
                marge={margeDuDebloquant(montre.bulle, v)}
                autres={debloquants.length > 1}
                garder={etatDeGarder(montre.bulle)}
                occupe={occupe}
                onOuvrir={ouvrir}
                onFait={faire}
                onUneAutre={() => setRang((r) => (r + 1) % debloquants.length)}
                onGarder={garderLa}
              />
            )}

            {/* La flottaison en étages : Surface, Léger, Plus lourd, puis Sous l'eau. */}
            <Etages etages={etagesDeMaintenant(v)} ouvert={toutVoir} onBasculer={basculerLeReste} onOuvrir={ouvrir} />
          </div>

          <div className="maintenant-droite" data-tuto="maintenant-droite">
            <BandeAVerifier
              lignes={aVerifier}
              phrase={phraseAVerifier(aVerifier, jour)}
              aujourdhui={jour}
              onOuvrir={ouvrir}
              onArrive={arrive}
              onRelancer={relancer}
            />
            {jd && parties.maJournee && <JourneeEnPostIt journee={jd.journee} aujourdhui={jd.aujourdhui} />}
          </div>
        </div>
      )}
    </div>
  );
}

export type { LigneVivier };
